import { useState, useEffect, useCallback } from 'react'
import processarCategoriaLeitura from '../pages/dashboardGestao/functions/processCategoriaLeitura'

import DashboardsPostagens from '../services/http/dashboards/DashboardsPostagens'
import { getMinDate } from '../pages/postagens/functions'

const getInitialFilters = () => ({
  ano_id: new Date().getFullYear(),
  mes_id: undefined,
  biblioteca_id: undefined,
  funcao_id: undefined,
  departamento_id: undefined
})

export function useGestaoDashboard() {
  const [filters, setFilters] = useState(getInitialFilters())
  const [ranking, setRanking] = useState({
    data: [],
    lastPage: 0,
    currentPage: 1
  })

  const [tipoPostagem, setTipoPostagem] = useState([])
  const [categoriasPostagens, setCategoriasPostagens] = useState([])
  const [loading, setLoading] = useState(false) // Um loading GERAL
  const [tableLoading, setTableLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [obraId, setObraId] = useState(null)

  const buscarRanking = useCallback(
    async (page = 1) => {
      setTableLoading(true)
      try {
        const { data } = await DashboardsPostagens().show({
          group_by: 'obra_titulo,obra_id',
          count: 'postagem_id',
          'order_by[count_postagem_id]': 'desc',
          status_postagem_id: [1, 2],
          page,
          ano_id: filters.ano_id,
          mes_id: filters.mes_id,
          leitor_biblioteca_id: filters.biblioteca_id, // Já adaptado
          leitor_funcao: filters.funcao_id,
          leitor_departamento: filters.departamento_id
        })
        setRanking({
          data: data.data.map(item => ({ ...item, id: item.obra_id })),
          lastPage: data.last_page,
          currentPage: data.current_page
        })
      } catch (error) {
        console.error(error)
        setRanking({ data: [], lastPage: 0, currentPage: 1 })
      } finally {
        setTableLoading(false)
      }
    },
    [filters]
  )

  const buscarGraficos = useCallback(async () => {
    setLoading(true)
    try {
      // Usamos Promise.all para disparar as buscas em paralelo
      await Promise.all([
        (async () => {
          // Lógica de `buscaPosts`
          const { data } = await DashboardsPostagens().show({
            per_page: -1,
            group_by: 'mes_id,categoira_descricao',
            count: 'postagem_id',
            status_postagem_id: [1, 2],
            ...filters, // Passa os filtros direto
            leitor_biblioteca_id: filters.biblioteca_id, // Adaptação
            leitor_funcao: filters.funcao_id,
            leitor_departamento: filters.departamento_id
          })
          setCategoriasPostagens(processarCategoriaLeitura(data.data))
        })(),
        (async () => {
          // Lógica de `tipoPostagens`
          // Quando há filtro de mês, agrupar por mes_id é semanticamente mais correto
          const groupBy = filters.mes_id ? 'mes_id' : 'ano_id'
          const { data } = await DashboardsPostagens().show({
            group_by: groupBy,
            count: 'resumo,video',
            per_page: -1,
            status_postagem_id: [1, 2],
            ...filters,
            leitor_biblioteca_id: filters.biblioteca_id, // Adaptação
            leitor_funcao: filters.funcao_id,
            leitor_departamento: filters.departamento_id
          })
          if (data.data[0]) {
            const item = data.data[0]
            setTipoPostagem([item.sum_resumo, item.sum_video])
          } else {
            setTipoPostagem([])
          }
        })()
      ])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [filters]) // Depende dos filtros

  // --- EFEITOS ---
  // Um único useEffect para reagir às mudanças de filtro
  useEffect(() => {
    // Roda as buscas principais quando os filtros mudam
    buscarRanking()
    buscarGraficos()
  }, [filters, buscarRanking, buscarGraficos])

  const handleFilterChange = newFilterValues => {
    setFilters(prev => ({ ...prev, ...newFilterValues }))
  }

  const handleMesChange = event => {
    setFilters(prev => ({ ...prev, mes_id: event?.mes }))
  }

  const handleAnoChange = date => {
    setFilters(prev => ({
      ...prev,
      ano_id: date?.$y || getMinDate().getFullYear()
    }))
  }

  const handlePageChange = (event, value) => {
    buscarRanking(value)
  }

  const handleOpenModal = id => {
    setObraId(id)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setObraId(null)
  }

  return {
    ranking,
    tipoPostagem,
    categoriasPostagens,
    filters,

    loading: loading || tableLoading,
    tableLoading,
    openModal,
    obraId,

    handleFilterChange,
    handleMesChange,
    handleAnoChange,
    handlePageChange,
    handleOpenModal,
    handleCloseModal
  }
}
